<template>
  <v-row v-loading="!dataLoaded">
    <v-col cols="12" class="pt-0">
      <PageTitle title="Просмотр заявления"/>
    </v-col>

    <v-col cols="12" class="d-flex justify-space-between pb-0">
      <v-btn
        small
        outlined
        rounded
        color="primary"
        class="white--text px-4 mr-2"
        @click="$router.push({name: 'applications'})"
      >
        <v-icon left small>mdi-chevron-left</v-icon>
        Назад
      </v-btn>

      <div>
        <template v-if="checkRoles">
          <v-btn
            small
            outlined
            rounded
            :loading="btnLoading"
            :disabled="!!application.curator"
            color="success"
            class="white--text px-4 mr-2"
            @click="appointCurator"
          >
            <v-icon left small>mdi-check-circle-outline</v-icon>
            Взять в обработку
          </v-btn>

          <v-btn
            small
            outlined
            :disabled="!application.curator || application.applicationStatus?.id !== 2"
            rounded
            color="success"
            class="white--text px-4 mr-2"
            @click="typeDialog = 'approveApp'"
          >
            <v-icon left small>mdi-check-circle-outline</v-icon>
            Принять
          </v-btn>

          <v-btn
            small
            outlined
            rounded
            :disabled="!application.curator || application.applicationStatus?.id !== 2"
            color="warning"
            class="white--text px-4 mr-2"
            @click="typeDialog = 'modificationApp'"
          >
            <v-icon left small>mdi-alert-circle-outline</v-icon>
            Доработка
          </v-btn>

          <v-btn
            small
            outlined
            rounded
            :disabled="!application.curator || ![2, 4].includes(application.applicationStatus?.id)"
            color="red"
            class="white--text px-4 mr-2"
            @click="typeDialog = 'rejectApp'"
          >
            <v-icon left small>mdi-cancel</v-icon>
            Отклонить
          </v-btn>
        </template>

        <template v-else>
          <v-btn
            small
            outlined
            rounded
            color="success"
            :disabled="![1, 3].includes(application.applicationStatus?.id)"
            class="white--text px-4 mr-2"
            @click="sendDialog = true"
          >
            <v-icon left small>mdi-check-circle-outline</v-icon>
            Отправить заявление
          </v-btn>
        </template>

        <v-btn
          small
          outlined
          rounded
          color="success"
          class="white--text px-4 mr-2"
          @click="typeDialog = 'checkMsg'"
        >
          <v-icon left small>mdi-email-outline</v-icon>
          Сообщения к заявлению
        </v-btn>
      </div>
    </v-col>

    <v-col cols="3">
      <PersonalDataCard
        class="mb-3"
        :personal-data="application.personalData"
      />
    </v-col>

    <v-col cols="6" class="px-0">
      <DocumentPreviewCard
        :values="selectedItem"
        :trigerShowPrint="triggerShowPrint"
      />

      <ApplicationDataCard
        class="my-3"
        :application="application"
      />
    </v-col>

    <v-col cols="3">
      <DocumentsCard
        :application="application"
        @selectItem="previewDocument"
      />
    </v-col>

    <UniversalConfirmDialog
      :type="typeDialog"
      :messages="application.messages"
      :loading="btnLoading"
      @confirm="confirm"
      @closeDialog="closeDialog"
    />

    <SendDialog
      :dialog="sendDialog"
      :loading="sendLoading"
      :print-loading="printLoading"
      @get-print="getPrint"
      @confirm="send"
      @closeDialog="closeDialog"
    />

  </v-row>
</template>

<script>
import PageTitle from '@/components/ui/Title'

import PersonalDataCard from '@/components/zvp/applicationRead/PersonalDataCard'
import DocumentPreviewCard from '@/components/zvp/applicationRead/DocumentPreviewCard'
import DocumentsCard from '@/components/zvp/applicationRead/DocumentsCard'
import ApplicationDataCard from '@/components/zvp/applicationRead/application-data-card/Index'

import UniversalConfirmDialog from '@/components/dialogs/UniversalDialog'
import SendDialog from "@/components/dialogs/SendDialog";

import { Application } from '@/models'
import {applicationApi, fileApi, printApi} from '@/api'
import { mapGetters } from 'vuex'
import download from "@/plugins/download";

export default {
  name: "ApplicationRead",

  components: {
    PageTitle,
    PersonalDataCard,
    ApplicationDataCard,
    DocumentPreviewCard,
    DocumentsCard,
    UniversalConfirmDialog,
    SendDialog
  },

  async created() {
    await this.getApplication()
    this.dataLoaded = true
  },

  data: () => ({
    application: new Application(),
    dataLoaded: false,

    selectedItem: null,
    triggerShowPrint: false,

    btnLoading: false,
    sendLoading: false,
    printLoading: false,

    typeDialog: null,
    sendDialog: false
  }),

  computed: {
    ...mapGetters('user', ['isCurator', 'isAdmin']),

    checkRoles() {
      return (this.isCurator || this.isAdmin)
    },
  },

  methods: {
    async getApplication() {
      try {
        const data = await applicationApi.getById(this.$route.params.id)
        if (data) {
          this.application = Application.build(data)
        } else {
          await this.$router.push({name: 'applications'})
        }
      } catch (e) {
        this.showErrorMessage(e)
      }
    },

    async appointCurator() {
      try {
        this.btnLoading = true
        const data = await applicationApi.appointCurator(this.$route.params.id)
        this.application = Application.build(data)
      } catch (e) {
        this.showErrorMessage(e)
      } finally {
        this.btnLoading = false
      }
    },

    async send(files) {
      try {
        this.sendLoading = true
        const data = await this.sendFiles(files)
        data && await this.sendApplication()
        this.sendDialog = false
      } catch (e) {
        this.showErrorMessage(e)
      } finally {
        this.sendLoading = false
      }
    },

    async sendApplication() {
      try {
        const data = await applicationApi.sendApplication(this.application.id)
        this.application = Application.build(data)
      } catch (e) {
        this.showErrorMessage(e)
      }
    },

    async sendFiles(files) {
      try {
        for (const el of files) {
          await fileApi.postFiles(el, this.application.id, 'Application')
        }
        return true
      } catch (e) {
        this.showErrorMessage(e)
      }
    },

    previewDocument(val) {
      this.selectedItem = val
      this.triggerShowPrint = !this.triggerShowPrint
    },

    async confirm(body) {
      this.btnLoading = true
      try {
        let data
        switch (this.typeDialog) {
          case 'approveApp':
            data = await applicationApi.acceptApplication(this.$route.params.id)
            break
          case 'modificationApp':
            data = await applicationApi.returnApplication(this.$route.params.id, body)
            break
          case 'rejectApp':
            data = await applicationApi.rejectApplication(this.$route.params.id, body)
            break
        }
        this.application = Application.build(data)
        this.closeDialog()
      } catch (e) {
        this.showErrorMessage(e)
      } finally {
        this.btnLoading = false
      }
    },

    async getPrint() {
      try {
        this.printLoading = true
        let data
        switch (this.application.applicationType?.id) {
          case 1: data = await printApi.applicationReinstate(this.application.id)
            break
          case 2: data = await printApi.applicationTransfer(this.application.id)
            break
          case 3: data = await printApi.applicationProgramTransfer(this.application.id)
            break
        }
        data && download(data, `${this.application.applicationType.name}.pdf`)
      } catch (e) {
        this.showErrorMessage(e)
      } finally {
        this.printLoading = false
      }
    },

    closeDialog() {
      this.typeDialog = null
      this.btnLoading = false
      this.sendDialog = false
    }
  },
}
</script>

<style scoped>
</style>
